import React, { useState, useEffect } from "react";

import {
  Button,
  DocumentList,
  ExpandablePanel,
  ExpandablePanelBody,
  ExpandablePanelHeader,
  Modal,
  TextArea,
  TextField,
  Typography,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as Download } from "@king-ict/eupisi-ui/components/Icon/Download.svg";
import { useInput, useValidation } from "@king-ict/eupisi-ui/hooks";
import { DocumentData } from "@king-ict/eupisi-ui/models";
import { isEmpty, nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import {
  getInitialConversationByRequestId as getInitialConversationByRequestIdApi,
  getInitialConversationComments as getInitialConversationCommentsApi,
  getInitialConversationDocument as getInitialConversationDocumentApi,
  getInitialConversationExportFile as getInitialConversationExportFileApi,
  createInitialConversationComment as createInitialConversationCommentApi,
  updateInitialConversationComment as updateInitialConversationCommentApi,
} from "../../../../api";
import { useTypedSelector } from "../../../../app";
import { Comment } from "../../../../components/Comment";
import { usePermission } from "../../../../hooks";
import { useBusinessRule } from "../../../../hooks";
import {
  InitialConversationQuestionGroup,
  InitialConversationQuestionDataItem,
  RequestComment,
  CreateUpdateRequestComment,
  RequestCommentValidationSchema,
  PermissionEnum,
  RequestPersonInfo,
} from "../../../../models";
import styles from "./RequestInitialConversationPage.module.scss";

export interface RequestInitialConversationPageProps {
  requestPersonInfo: RequestPersonInfo;
}

export const RequestInitialConversationPage = ({
  requestPersonInfo,
}: RequestInitialConversationPageProps) => {
  const { requestId } = useParams<{ requestId: string }>();
  const user = useTypedSelector((state) => state.application.user);
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const emptyValue = "-";

  const [
    requestInitialConversationData,
    setRequestInitialConversationData,
  ] = useState<InitialConversationQuestionGroup[]>(
    [] as InitialConversationQuestionGroup[]
  );

  const { canCreateUpdateCommentRequest } = useBusinessRule();

  const [
    initialConversationComments,
    setInitialConversationComments,
  ] = useState<RequestComment[]>([] as RequestComment[]);

  const [newComment, setNewComment] = useState<CreateUpdateRequestComment>(
    {} as CreateUpdateRequestComment
  );

  const [errors, validate] = useValidation({
    data: newComment,
    validationSchema: RequestCommentValidationSchema,
  });

  const [
    openCommentConfirmationModal,
    setOpenCommentConfirmationModal,
  ] = useState(false);

  const [
    openCommentEditConfirmationModal,
    setOpenCommentEditConfirmationModal,
  ] = useState(false);

  const [openCommentSavedModal, setOpenCommentSavedModal] = useState(false);

  const handleToggleCommentConfirmationModal = () => {
    if (validate()) {
      setOpenCommentConfirmationModal(
        (prevOpenCommentConfirmationModal) => !prevOpenCommentConfirmationModal
      );
    }
  };

  const handleToggleCommentEditConfirmationModal = () => {
    if (validate()) {
      setOpenCommentEditConfirmationModal(
        (prevOpenCommentEditConfirmationModal) =>
          !prevOpenCommentEditConfirmationModal
      );
    }
  };

  const handleToggleCommentSavedModal = () => {
    setOpenCommentSavedModal(
      (prevOpenCommentSavedModal) => !prevOpenCommentSavedModal
    );
  };

  const { handleChange } = useInput();

  useEffect(() => {
    (async () => {
      if (requestId) {
        const requestInitialConversationData = await getInitialConversationByRequestIdApi(
          +requestId
        );
        setRequestInitialConversationData(requestInitialConversationData);
      }
    })();
  }, [requestId]);

  useEffect(() => {
    (async () => {
      const initialConversationCommentsData = await getInitialConversationCommentsApi(
        +requestId
      );
      if (initialConversationCommentsData.length > 0) {
        setInitialConversationComments(initialConversationCommentsData);
      }
    })();
  }, [requestId]);

  const handleDownloadInitialConversationDocument = (
    document: DocumentData
  ) => {
    if (document.fileID) {
      getInitialConversationDocumentApi(document.fileID);
    }
  };

  const renderQuestionGroups = () => {
    if (requestInitialConversationData.length > 0) {
      return requestInitialConversationData.map((questionGroup, index) => {
        return (
          <React.Fragment key={questionGroup.id}>
            <Row className={styles.rowSpace}>
              <Col md={12}>
                <ExpandablePanel clickableHeader>
                  <ExpandablePanelHeader medium>
                    {questionGroup.name}
                  </ExpandablePanelHeader>
                  <ExpandablePanelBody>
                    {renderQuestions(questionGroup.questionsData)}
                  </ExpandablePanelBody>
                </ExpandablePanel>
              </Col>
            </Row>
            {index < requestInitialConversationData.length - 1 && (
              <hr className={styles.line} />
            )}
          </React.Fragment>
        );
      });
    }
  };

  const renderQuestions = (
    questionsData: InitialConversationQuestionDataItem[]
  ) => {
    return (
      <>
        <Row className={`${styles.rowSpace} ${styles.withoutMargin}`}>
          {questionsData?.map((item) => (
            <Col
              className={styles.withoutPadding}
              md={12}
              key={item.questionGroupQuestionId}
            >
              {item.documents && item.documents.length > 0 ? (
                <>
                  <Typography variant="baseBold">
                    {t("common.documentsAdded")}
                  </Typography>
                  <DocumentList
                    documents={item.documents}
                    removable={false}
                    onDocumentClick={handleDownloadInitialConversationDocument}
                  />
                </>
              ) : (
                <TextField
                  type="text"
                  label={item.questionName}
                  value={item.answer || emptyValue}
                  plaintext
                  boldLabel
                  lightText
                />
              )}
            </Col>
          ))}
        </Row>
      </>
    );
  };

  const handleDownloadInitialConversationExport = () => {
    getInitialConversationExportFileApi(+requestId);
  };

  const renderComments = () => {
    return initialConversationComments.map((comment) => {
      return (
        <Comment
          key={comment.id}
          requestComment={comment}
          newComment={newComment}
          isEditable={
            hasPermission(PermissionEnum.RequestCommentCreate) &&
            comment.createdBy === user.userId &&
            canCreateUpdateCommentRequest(
              requestPersonInfo.enrollmentTypeId,
              requestPersonInfo.monthId
            )
          }
          onEditClick={handleCommentEditMode}
          onCancelClick={handleCancelClicked}
          onSaveClick={handleToggleCommentEditConfirmationModal}
          onChange={handleEditModeChange}
        />
      );
    });
  };

  const handleCommentEditMode = (commentId: number) => {
    const commentIndex = initialConversationComments.findIndex(
      (x) => x.id === commentId
    );

    const newInitialConversationComments = [...initialConversationComments];
    newInitialConversationComments.forEach((c) => (c.editing = false));

    newInitialConversationComments[commentIndex] = {
      ...newInitialConversationComments[commentIndex],
      editing: true,
    };

    setInitialConversationComments(newInitialConversationComments);
    const comment = {
      id: commentId,
      comment: initialConversationComments.find((c) => c.id === commentId)
        ?.comment,
    } as CreateUpdateRequestComment;
    setNewComment(comment);
  };

  const handleSaveComment = async () => {
    handleToggleCommentConfirmationModal();

    await createInitialConversationCommentApi(+requestId, newComment).then(() =>
      handleToggleCommentSavedModal()
    );
  };

  const handleEditComment = async () => {
    handleToggleCommentEditConfirmationModal();

    await updateInitialConversationCommentApi(newComment).then(() =>
      handleToggleCommentSavedModal()
    );
  };

  const handleCommentSavedModalClose = () => {
    handleToggleCommentSavedModal();
    window.location.reload();
  };

  const handleCancelClicked = () => {
    const newInitialConversationComments = [...initialConversationComments];
    newInitialConversationComments.forEach((c) => (c.editing = false));
    setInitialConversationComments(newInitialConversationComments);

    setNewComment({} as CreateUpdateRequestComment);
  };

  const handleEditModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e, setNewComment);
  };

  return (
    <>
      <Row className={`${styles.rowHeading} ${styles.withoutMargin}`}>
        <Col
          className={`${styles.withoutPadding} ${styles.conversationLayout}`}
          md={6}
        >
          <Col className={styles.withoutPadding} md={12}>
            <Typography variant="largeBold">
              {t("sidebar.initialConversation")}
            </Typography>
            {renderQuestionGroups()}
          </Col>
          {requestInitialConversationData?.length > 0 && (
            <Col
              md={12}
              className={`${styles.withoutPadding} ${styles.buttonsLayout} ${styles.buttonsRight}`}
            >
              <Button
                size="sm"
                color="primary"
                outline
                icon={<Download />}
                className={`${styles.buttonSpace}`}
                onClick={handleDownloadInitialConversationExport}
              >
                {t("request.initialConversation.download")}
              </Button>
            </Col>
          )}
        </Col>
        <Col className={`${styles.commentsLayout}`} md={6}>
          <Col className={`${styles.commentsTitle}`} md={12}>
            <Typography variant="smallHeading">
              {t("request.initialConversation.commentsTitle")}
            </Typography>
            {renderComments()}

            {initialConversationComments.every((c) => !c.editing) &&
              hasPermission(PermissionEnum.RequestCommentCreate) &&
              canCreateUpdateCommentRequest(
                requestPersonInfo.enrollmentTypeId,
                requestPersonInfo.monthId
              ) && (
                <>
                  <TextArea
                    type="text"
                    label={t("request.initialConversation.yourComment")}
                    ariaLabel={t("request.initialConversation.yourComment")}
                    rows={4}
                    value={newComment.comment || ""}
                    name={nameof<typeof newComment>("comment")}
                    onChange={(e) => handleChange(e, setNewComment)}
                    errorText={errors?.comment}
                  />
                  <div className={styles.commentButtons}>
                    <Button
                      className={styles.cancelButton}
                      outline
                      onClick={handleCancelClicked}
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      color="primary"
                      onClick={handleToggleCommentConfirmationModal}
                    >
                      {t("request.initialConversation.saveCommentButton")}
                    </Button>
                  </div>
                </>
              )}
          </Col>
        </Col>
      </Row>

      <Modal
        type={"confirm"}
        title={t(
          "request.initialConversation.commentSaveConfirmationModalMessage"
        )}
        confirmText={t("common.yes")}
        cancelText={t("common.no")}
        onConfirm={handleSaveComment}
        onClose={() => handleToggleCommentConfirmationModal()}
        open={openCommentConfirmationModal}
      />

      <Modal
        type={"confirm"}
        title={t(
          "request.initialConversation.commentEditConfirmationModalMessage"
        )}
        confirmText={t("common.yes")}
        cancelText={t("common.no")}
        onConfirm={handleEditComment}
        onClose={() => handleToggleCommentEditConfirmationModal()}
        open={openCommentEditConfirmationModal}
      />

      <Modal
        type={"message"}
        title={t("common.saveSuccess")}
        confirmText={t("common.close")}
        onConfirm={handleCommentSavedModalClose}
        onClose={() => handleCommentSavedModalClose()}
        open={openCommentSavedModal}
      />
    </>
  );
};
